import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { Badge } from '../ui/badge';
import {
  Target, Sparkles, Upload, Download, Trash2, Plus, CheckCircle2, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import TargetsHelpDrawer from './TargetsHelpDrawer';

const fmt = (n, d = 0) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: d, minimumFractionDigits: d });
const co2 = (kg) => (kg >= 1000 ? `${fmt(kg / 1000, 1)} t` : `${fmt(kg, 0)} kg`);

const YEARS = (() => {
  const y = new Date().getFullYear();
  return [y + 1, y, y - 1, y - 2];
})();

function Gauge({ pct, status }) {
  const p = Math.max(0, Math.min(pct || 0, 150));
  const frac = p / 150;
  const R = 70, CX = 90, CY = 90;
  const len = Math.PI * R;
  const color = status === 'over' ? '#ef4444' : (p >= 90 ? '#f59e0b' : '#10b981');
  const tFrac = 100 / 150;
  const ang = Math.PI * (1 - tFrac);
  const tx1 = CX + (R - 11) * Math.cos(ang), ty1 = CY - (R - 11) * Math.sin(ang);
  const tx2 = CX + (R + 11) * Math.cos(ang), ty2 = CY - (R + 11) * Math.sin(ang);
  const arc = `M ${CX - R} ${CY} A ${R} ${R} 0 0 1 ${CX + R} ${CY}`;
  return (
    <svg viewBox="0 0 180 104" className="w-full max-w-[220px]" data-testid="targets-gauge">
      <path d={arc} fill="none" stroke="#e2e8f0" strokeWidth="14" strokeLinecap="round" />
      <path d={arc} fill="none" stroke={color} strokeWidth="14" strokeLinecap="round"
            strokeDasharray={`${frac * len} ${len}`} style={{ transition: 'stroke-dasharray .6s ease' }} />
      <line x1={tx1} y1={ty1} x2={tx2} y2={ty2} stroke="#334155" strokeWidth="2" />
      <text x={CX} y={CY - 8} textAnchor="middle" className="fill-slate-900" style={{ fontSize: 22, fontWeight: 800 }}>
        {p ? `${fmt(p, 0)}%` : '—'}
      </text>
      <text x={CX} y={CY + 8} textAnchor="middle" className="fill-slate-400" style={{ fontSize: 9 }}>of target</text>
    </svg>
  );
}

const StatusBadge = ({ status }) => {
  if (status === 'on_track') return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"><CheckCircle2 className="mr-1 h-3 w-3" />On track</Badge>;
  if (status === 'over') return <Badge className="bg-red-100 text-red-700 hover:bg-red-100"><AlertTriangle className="mr-1 h-3 w-3" />Over target</Badge>;
  return <Badge variant="outline">No target</Badge>;
};

export default function EmissionsTargets({ accountId }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSet, setShowSet] = useState(false);
  const [showRec, setShowRec] = useState(false);
  const [rec, setRec] = useState(null);
  const [recLoading, setRecLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // set-target form
  const [form, setForm] = useState({
    scope: 'org', site_uid: '', target_type: 'reduction',
    target_co2e_kg: '', reduction_pct: '5', baseline_year: String(new Date().getFullYear() - 1),
  });

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const { data: d } = await api.get('/dashboard/org/mongo/emissions/targets', { params: { account_id: accountId, year } });
      setData(d);
    } catch {
      toast.error('Could not load emissions targets.');
    } finally { setLoading(false); }
  }, [accountId, year]);

  useEffect(() => { load(); }, [load]);

  const org = data?.org;
  const properties = data?.properties || [];
  const available = data?.available_properties || [];
  const rollup = data?.rollup;

  const saveTarget = async () => {
    try {
      if (form.scope === 'org') {
        await api.put('/dashboard/org/mongo/emissions/targets', {
          account_id: accountId, year,
          target_type: form.target_type,
          target_co2e_kg: form.target_type === 'absolute' ? Number(form.target_co2e_kg) : null,
          reduction_pct: form.target_type === 'reduction' ? Number(form.reduction_pct) : null,
          baseline_year: form.target_type === 'reduction' ? Number(form.baseline_year) : null,
        });
      } else {
        if (!form.site_uid) { toast.error('Pick a property.'); return; }
        const prop = available.find((p) => p.site_uid === form.site_uid);
        await api.post('/dashboard/org/mongo/emissions/targets/property', {
          account_id: accountId, year, site_uid: form.site_uid, sitename: prop?.sitename,
          target_type: form.target_type,
          target_co2e_kg: form.target_type === 'absolute' ? Number(form.target_co2e_kg) : null,
          reduction_pct: form.target_type === 'reduction' ? Number(form.reduction_pct) : null,
          baseline_year: form.target_type === 'reduction' ? Number(form.baseline_year) : null,
        });
      }
      toast.success('Target saved.');
      setShowSet(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save target.');
    }
  };

  const openRecommend = async () => {
    setShowRec(true);
    setRecLoading(true);
    setRec(null);
    try {
      const { data: r } = await api.get('/dashboard/org/mongo/emissions/targets/recommend', {
        params: { account_id: accountId, year, site_uid: form.scope === 'property' ? form.site_uid : undefined },
      });
      setRec(r);
    } catch {
      toast.error('Could not compute a recommendation.');
    } finally { setRecLoading(false); }
  };

  const applyRecommendation = async (opt) => {
    try {
      if (form.scope === 'property' && form.site_uid) {
        const prop = available.find((p) => p.site_uid === form.site_uid);
        await api.post('/dashboard/org/mongo/emissions/targets/property', {
          account_id: accountId, year, site_uid: form.site_uid, sitename: prop?.sitename,
          target_type: 'reduction', reduction_pct: opt.reduction_pct, baseline_year: rec.baseline_year,
        });
      } else {
        await api.put('/dashboard/org/mongo/emissions/targets', {
          account_id: accountId, year, target_type: 'reduction',
          reduction_pct: opt.reduction_pct, baseline_year: rec.baseline_year,
        });
      }
      toast.success('Recommended target applied.');
      setShowRec(false);
      load();
    } catch {
      toast.error('Could not apply the target.');
    }
  };

  const deleteTarget = async (id) => {
    try {
      await api.delete(`/dashboard/org/mongo/emissions/targets/${id}`, { params: { account_id: accountId } });
      toast.success('Target removed.');
      load();
    } catch { toast.error('Could not remove target.'); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get('/dashboard/org/mongo/emissions/targets/template', { responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.data);
      a.download = 'emissions-targets-template.csv';
      a.click();
    } catch { toast.error('Could not download the template.'); }
  };

  const uploadTargets = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data: r } = await api.post('/dashboard/org/mongo/emissions/targets/upload', fd, {
        params: { account_id: accountId, year },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Imported ${r.count} target${r.count === 1 ? '' : 's'}${r.errors?.length ? ` · ${r.errors.length} skipped` : ''}.`);
      load();
    } catch {
      toast.error('Could not upload targets.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Card className="border-slate-200/70 shadow-none" data-testid="emissions-targets">
      <CardContent className="p-4">
        {/* Header */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <Target className="h-4 w-4 text-emerald-600" /> Emissions targets
          </div>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="h-8 w-[92px] text-xs" data-testid="targets-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {YEARS.map((y) => <SelectItem key={y} value={String(y)} data-testid={`targets-year-${y}`}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <TargetsHelpDrawer />
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" onClick={openRecommend} data-testid="targets-recommend-btn">
              <Sparkles className="h-3.5 w-3.5" /> Recommend
            </Button>
            <Button size="sm" className="h-8 gap-1.5 text-xs" onClick={() => setShowSet(true)} data-testid="targets-set-btn">
              <Plus className="h-3.5 w-3.5" /> Set target
            </Button>
          </div>
        </div>

        {/* Org gauge */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="flex flex-col items-center justify-center rounded-md border border-slate-100 bg-slate-50/50 p-4" data-testid="targets-org-gauge">
            {org ? (
              <>
                <Gauge pct={org.pct_of_target} status={org.status} />
                <div className="mt-1"><StatusBadge status={org.status} /></div>
              </>
            ) : rollup && rollup.coverage?.with_targets > 0 ? (
              <>
                <Gauge pct={rollup.pct_of_target} status={rollup.status} />
                <div className="mt-1"><StatusBadge status={rollup.status} /></div>
                <div className="mt-1 text-[10px] text-slate-500" data-testid="targets-rollup-caption">
                  Rolled up from {rollup.coverage.with_targets} propert{rollup.coverage.with_targets === 1 ? 'y' : 'ies'} target{rollup.coverage.with_targets === 1 ? '' : 's'}
                </div>
              </>
            ) : (
              <div className="py-8 text-center">
                <Target className="mx-auto mb-2 h-8 w-8 text-slate-300" />
                <div className="text-xs text-slate-500">No portfolio target for {year} yet.</div>
                <Button size="sm" variant="link" className="mt-1 h-auto p-0 text-xs text-emerald-700" onClick={() => setShowSet(true)} data-testid="targets-empty-set">
                  Set a goal →
                </Button>
              </div>
            )}
          </div>

          <div className="md:col-span-2 grid grid-cols-2 gap-3 self-center">
            <Stat label={`${year} actual`} value={`${co2(data?.actual_org_kg || 0)} CO₂e`} testid="targets-actual" />
            <Stat
              label="Target (goal)"
              value={org ? `${co2(org.effective_target_kg)} CO₂e` : (rollup?.coverage?.with_targets > 0 ? `${co2(rollup.effective_target_kg)} CO₂e` : '—')}
              sub={rollup?.coverage?.with_targets > 0 ? `Property roll-up: ${co2(rollup.effective_target_kg)} · covers ${rollup.coverage.with_targets}/${rollup.coverage.total}` : null}
              testid="targets-goal"
            />
            <Stat
              label="Remaining budget"
              value={org ? co2(Math.max(org.remaining_kg, 0)) : '—'}
              sub={org && org.remaining_kg < 0 ? `${co2(Math.abs(org.remaining_kg))} over` : null}
              danger={org && org.remaining_kg < 0}
              testid="targets-remaining"
            />
            <Stat
              label="Goal type"
              value={org ? (org.target_type === 'reduction' ? `−${fmt(org.reduction_pct, 1)}% vs ${org.baseline_year}` : 'Absolute') : '—'}
              testid="targets-type"
            />
          </div>
        </div>

        {/* Property targets */}
        <div className="mt-5">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Property targets</div>
            <div className="ml-auto flex items-center gap-2">
              <Button size="sm" variant="ghost" className="h-7 gap-1.5 text-xs text-slate-600" onClick={downloadTemplate} data-testid="targets-template-btn">
                <Download className="h-3.5 w-3.5" /> Template
              </Button>
              <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={uploadTargets} data-testid="targets-upload-input" />
              <Button size="sm" variant="ghost" className="h-7 gap-1.5 text-xs text-slate-600" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="targets-upload-btn">
                <Upload className="h-3.5 w-3.5" /> {uploading ? 'Uploading…' : 'Upload CSV'}
              </Button>
            </div>
          </div>
          <div className="max-h-[280px] overflow-y-auto rounded-md border border-slate-100">
            <Table>
              <TableHeader className="sticky top-0 bg-white z-10">
                <TableRow>
                  <TableHead className="text-[10px] uppercase">Property</TableHead>
                  <TableHead className="text-[10px] uppercase text-right">Actual</TableHead>
                  <TableHead className="text-[10px] uppercase text-right">Target</TableHead>
                  <TableHead className="text-[10px] uppercase w-40">Progress</TableHead>
                  <TableHead className="text-[10px] uppercase w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {properties.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="py-6 text-center text-xs text-slate-400">No property targets yet. Use “Set target”, “Recommend”, or upload a CSV.</TableCell></TableRow>
                )}
                {properties.map((t, i) => {
                  const pct = Math.max(0, Math.min(t.pct_of_target || 0, 100));
                  return (
                    <TableRow key={t.id} data-testid={`targets-property-row-${i}`}>
                      <TableCell className="text-xs text-slate-800">{t.sitename || t.site_uid}</TableCell>
                      <TableCell className="text-xs text-right font-mono">{co2(t.actual_kg)}</TableCell>
                      <TableCell className="text-xs text-right font-mono">{co2(t.effective_target_kg)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: t.status === 'over' ? '#ef4444' : '#10b981' }} />
                          </div>
                          <span className="w-10 text-right text-[10px] font-mono text-slate-500">{fmt(t.pct_of_target, 0)}%</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-slate-400 hover:text-red-600" onClick={() => deleteTarget(t.id)} data-testid={`targets-property-delete-${i}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      </CardContent>

      {/* Set target dialog */}
      <Dialog open={showSet} onOpenChange={setShowSet}>
        <DialogContent data-testid="targets-set-dialog">
          <DialogHeader>
            <DialogTitle>Set an emissions target · {year}</DialogTitle>
            <DialogDescription>Define a CO₂e goal for the whole portfolio or a single property.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Scope</Label>
                <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                  <SelectTrigger className="h-9 text-sm" data-testid="targets-form-scope"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="org">Whole portfolio</SelectItem>
                    <SelectItem value="property">Single property</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Target type</Label>
                <Select value={form.target_type} onValueChange={(v) => setForm({ ...form, target_type: v })}>
                  <SelectTrigger className="h-9 text-sm" data-testid="targets-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reduction">% reduction</SelectItem>
                    <SelectItem value="absolute">Absolute CO₂e</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {form.scope === 'property' && (
              <div>
                <Label className="text-xs">Property</Label>
                <Select value={form.site_uid} onValueChange={(v) => setForm({ ...form, site_uid: v })}>
                  <SelectTrigger className="h-9 text-sm" data-testid="targets-form-property"><SelectValue placeholder="Choose a property" /></SelectTrigger>
                  <SelectContent>
                    {available.map((p) => <SelectItem key={p.site_uid} value={p.site_uid}>{p.sitename} ({co2(p.actual_kg)})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {form.target_type === 'absolute' ? (
              <div>
                <Label className="text-xs">Yearly goal (kg CO₂e)</Label>
                <Input type="number" value={form.target_co2e_kg} onChange={(e) => setForm({ ...form, target_co2e_kg: e.target.value })} placeholder="e.g. 120000" data-testid="targets-form-absolute" />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Reduction %</Label>
                  <Input type="number" value={form.reduction_pct} onChange={(e) => setForm({ ...form, reduction_pct: e.target.value })} placeholder="e.g. 5" data-testid="targets-form-reduction" />
                </div>
                <div>
                  <Label className="text-xs">Baseline year</Label>
                  <Input type="number" value={form.baseline_year} onChange={(e) => setForm({ ...form, baseline_year: e.target.value })} data-testid="targets-form-baseline" />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSet(false)}>Cancel</Button>
            <Button onClick={saveTarget} data-testid="targets-form-save">Save target</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recommend dialog */}
      <Dialog open={showRec} onOpenChange={setShowRec}>
        <DialogContent data-testid="targets-recommend-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-emerald-600" /> Recommended targets · {year}</DialogTitle>
            <DialogDescription>
              {recLoading ? 'Crunching last year’s data…' : rec ? `Based on ${rec.baseline_year} actual of ${co2(rec.baseline_kg)} CO₂e.` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {recLoading && <div className="py-6 text-center text-sm text-slate-400">Loading…</div>}
            {!recLoading && rec?.options?.map((opt) => (
              <div key={opt.basis} className="flex items-center justify-between rounded-md border border-slate-200 p-3" data-testid={`targets-rec-option-${opt.basis}`}>
                <div>
                  <div className="text-sm font-semibold text-slate-900">{opt.label}</div>
                  <div className="text-xs text-slate-500">Goal: {co2(opt.target_co2e_kg)} CO₂e (−{fmt(opt.reduction_pct, 1)}%)</div>
                </div>
                <Button size="sm" onClick={() => applyRecommendation(opt)} data-testid={`targets-rec-apply-${opt.basis}`}>Use this</Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

const Stat = ({ label, value, sub, danger, testid }) => (
  <div className="rounded-md border border-slate-100 p-3" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
    <div className="mt-0.5 text-lg font-bold text-slate-900">{value}</div>
    {sub && <div className={`text-[11px] font-medium ${danger ? 'text-red-600' : 'text-slate-500'}`}>{sub}</div>}
  </div>
);
